"use client";

import { useState, useMemo } from "react";
import Link from "next/link";

interface TradeCard {
  name: string;
  slug: string;
  description: string;
}

interface TradeCategory {
  heading: string;
  trades: TradeCard[];
}

interface PopularLocation {
  name: string;
  slug: string;
  tradeSlug: string;
  tradeLabel: string;
}

interface Props {
  categories: TradeCategory[];
  popularTrades: TradeCard[];
  popularLocations: PopularLocation[];
  locationCount: number;
}

export default function FindClient({
  categories,
  popularTrades,
  popularLocations,
  locationCount,
}: Props) {
  const [query, setQuery] = useState("");

  const filteredCategories = useMemo(() => {
    if (!query.trim()) return categories;
    const q = query.toLowerCase().trim();
    return categories
      .map((cat) => ({
        ...cat,
        trades: cat.trades.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.description.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.trades.length > 0);
  }, [query, categories]);

  const filteredPopular = useMemo(() => {
    if (!query.trim()) return popularTrades;
    const q = query.toLowerCase().trim();
    return popularTrades.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
    );
  }, [query, popularTrades]);

  const isFiltering = query.trim().length > 0;

  return (
    <>
      {/* Sticky search */}
      <div className="sticky top-16 z-30 bg-warm-bg/95 backdrop-blur-sm py-4 -mx-5 px-5 border-b border-stone/50">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search trades (e.g. plumber, roofer, kitchen)..."
            className="w-full pl-10 pr-4 py-3 rounded-sm border border-stone bg-white text-text-body placeholder:text-text-muted text-sm focus:outline-none focus:border-forest focus:ring-1 focus:ring-forest"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-charcoal"
              aria-label="Clear search"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Popular locations */}
      {!isFiltering && (
        <div className="mt-6 mb-8">
          <p className="text-sm font-medium text-text-body mb-3">
            Popular locations
          </p>
          <div className="flex flex-wrap gap-2">
            {popularLocations.map((loc) => (
              <Link
                key={loc.slug}
                href={`/find/${loc.tradeSlug}/${loc.slug}`}
                className="px-3 py-1.5 text-sm rounded-full border border-stone bg-white text-text-body no-underline hover:border-forest hover:text-forest transition-colors"
              >
                {loc.tradeLabel} in {loc.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Popular trades */}
      {filteredPopular.length > 0 && (
        <section className="mb-10">
          {!isFiltering && (
            <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
              Popular Trades
            </h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPopular.map((trade) => (
              <Link
                key={trade.slug}
                href={`/find/${trade.slug}`}
                className="group block bg-white rounded-md border-2 border-sage-light/50 p-6 no-underline transition-all hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:border-forest"
              >
                <h3 className="font-serif text-lg font-semibold text-charcoal group-hover:text-forest transition-colors">
                  {trade.name}
                </h3>
                <p className="text-text-muted text-sm mt-1.5">
                  {trade.description}
                </p>
                <p className="text-xs text-forest mt-3 font-medium">
                  {locationCount}+ locations
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Categorised trades */}
      {filteredCategories.map((category) => (
        <section key={category.heading} className="mb-10">
          <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">
            {category.heading}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {category.trades.map((trade) => (
              <Link
                key={`${category.heading}-${trade.slug}`}
                href={`/find/${trade.slug}`}
                className="group block bg-white rounded-md border border-border-light p-4 no-underline transition-all hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:border-sage-light"
              >
                <h3 className="font-serif text-base font-semibold text-charcoal group-hover:text-forest transition-colors">
                  {trade.name}
                </h3>
                <p className="text-text-muted text-sm mt-1">
                  {trade.description}
                </p>
                <p className="text-xs text-forest mt-2 font-medium">
                  {locationCount}+ locations
                </p>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* No results */}
      {isFiltering &&
        filteredCategories.length === 0 &&
        filteredPopular.length === 0 && (
          <div className="text-center py-12">
            <p className="text-text-muted text-lg">
              No trades matching &ldquo;{query}&rdquo;
            </p>
            <p className="text-text-muted text-sm mt-2">
              Try a different search term or{" "}
              <button
                onClick={() => setQuery("")}
                className="text-forest underline"
              >
                browse all trades
              </button>
            </p>
          </div>
        )}
    </>
  );
}
